/**
 * Reads every row a query matches, past the 1,000 row cap on one request.
 *
 * Every page is ordered by id, so pages can never overlap or skip rows while
 * reading. If a row still shows up twice (a row edited between two pages), the
 * copies are merged by id and the count is logged. This never throws on a
 * duplicate: a screen with a warning beats a screen with nothing.
 *
 * A byte identical copy lives in supabase/functions/_shared/fetchAllRows.ts
 * for backend functions; a test keeps the two equal.
 *
 * Only use this when every row is needed. When a screen only shows a number,
 * ask for a count instead (select with { count: "exact", head: true }).
 */

export const PAGE_SIZE = 1000;

// deno-lint-ignore no-explicit-any
type PageQuery = { order: (col: string, opts: { ascending: boolean }) => any };

export async function fetchAllRows<T extends { id: string }>(
  /** Builds a fresh filtered query each call. Do not add order or range. */
  makeQuery: () => PageQuery,
  label: string,
  pageSize: number = PAGE_SIZE,
): Promise<T[]> {
  const byId = new Map<string, T>();
  let total = 0;
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await makeQuery()
      .order("id", { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data ?? []) as T[];
    total += rows.length;
    for (const row of rows) byId.set(row.id, row);
    if (rows.length < pageSize) break;
  }
  if (byId.size !== total) {
    console.warn(`[fetchAllRows] ${label}: ${total - byId.size} duplicate rows merged by id`, {
      read: total,
      unique: byId.size,
    });
  }
  return [...byId.values()];
}
